import { useLocation } from "react-router";

import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { ArrowRightIcon } from "@/components/Icons/ArrowRightIcon";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import { Button } from "@/design-system/Button";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { sentimentColors, SentimentDistributionChart } from "@/modules/analytics/components/SentimentDistributionChart";
import type {
  CommunityFeedAnalyticsDateRange,
  CommunityFeedSentimentAnalytics,
} from "@/queries/analytics/community-feed-reporting-types";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface Props {
  sentiment: CommunityFeedSentimentAnalytics;
  dateRange: CommunityFeedAnalyticsDateRange;
}

export function SentimentSummaryCard({ sentiment, dateRange }: Props): React.ReactNode {
  const { t } = useTranslation();
  const analyticsScope = useAnalyticsScope();
  const location = useLocation();
  const slug = useSlug({ optional: true });
  const sentimentDetailsHref =
    analyticsScope.type === "portfolio"
      ? addPortfolioProjectFilterQueryParams(
          `${routes.portfolio.analyticsSentiment()}?dateRange=${dateRange}`,
          location.search,
        )
      : `${routes.analytics.communityFeedSentimentReporting({ slug: slug! })}?dateRange=${dateRange}`;
  const labels = {
    analyzedPosts: t("page.analytics-community-feed.sentiment.analyzed-posts"),
    insufficientData: t("page.analytics-community-feed.sentiment.insufficient-month"),
    negative: t("page.analytics-community-feed.sentiment.negative"),
    neutral: t("page.analytics-community-feed.sentiment.neutral"),
    positive: t("page.analytics-community-feed.sentiment.positive"),
    currentPeriod: t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress"),
    yAxis: t("page.analytics-community-feed.sentiment.axis-share"),
  };

  return (
    <PageGrid.Column size="100%">
      <section className="flex size-full flex-col items-start gap-space-md">
        <header className="flex w-full flex-col gap-space-sm">
          <h2 className="text-heading-2 text-text-brand-secondary">
            {t("page.analytics-community-feed.sentiment.summary-section")}
          </h2>
          <span className="text-caption text-text-secondary">
            {t("page.analytics-community-feed.sentiment.summary-description")}
          </span>
        </header>
        <AnalyticsCard.Root variant="sentimentGraph">
          <AnalyticsCard.Header
            title={t("page.analytics-community-feed.sentiment.distribution-title")}
            tooltip={t("page.analytics-community-feed.sentiment.method-tooltip", {
              count: sentiment.minimumSampleSize,
            })}
          >
            <Button type="link" variant="subtle" size="sm" icon={<ArrowRightIcon />} href={sentimentDetailsHref}>
              {t("page.analytics-community-feed.sentiment.view-details")}
            </Button>
          </AnalyticsCard.Header>
          <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
            <SentimentDistributionChart data={sentiment.monthly} labels={labels} />
            <ChartLegend
              items={[
                { label: labels.positive, color: sentimentColors.positive },
                { label: labels.neutral, color: sentimentColors.neutral },
                { label: labels.negative, color: sentimentColors.negative },
                {
                  label: t("page.analytics-details.engagement.chart.legend.current-period"),
                  color: dataColors.primary,
                  pattern: "striped",
                },
              ]}
            />
          </figure>
        </AnalyticsCard.Root>
      </section>
    </PageGrid.Column>
  );
}
